/* LK SWYM Hybrid Loader v1
 * Goal: keep LK local wishlist instant, lazy-load SWYM only after real wishlist intent (D-206) or, once
 * per device and after the page is idle, for a logged-in customer with device-only favorites (D-243).
 * The Wishlist Plus app embed stays off, so this file sets the globals the embed would set before
 * injecting the SDK. Fixed values live only in window.LK_SWYM_CONFIG (layout/theme.liquid):
 * { retailerId, storePath, jsPath, sdkSrc }. Every SDK call runs inside window.SwymCallbacks.
 * Change: openspec/changes/wishlist-swym-restore.
 */
(function () {
  'use strict';
  if (window.__LK_SWYM_HYBRID_V1__) return;
  window.__LK_SWYM_HYBRID_V1__ = true;

  var cfg = window.LK_SWYM_CONFIG || {};
  // D-212: device-only favorites go up once per device (lk_wishlist is per device), marked, capped and spaced.
  var RECOVERY_FLAG = 'lk_swym_recovered';
  var RECOVERY_CAP = 50;
  var RECOVERY_GAP_MS = 400;
  // D-243: that recovery also runs without a tap: at most AUTO_MAX_TRIES automatic tries, 24 h apart,
  // stored as "<ISO time>|<tries>", so a permanent failure (blocked swymrelay.com, no link) stops.
  var AUTO_TRY_KEY = 'lk_swym_recovery_try';
  var AUTO_BACKOFF_MS = 24 * 60 * 60 * 1000;
  var AUTO_MAX_TRIES = 3;
  var AUTO_DELAY_MS = 2000;
  // ponytail: Storefront API version pinned; bump it before Shopify stops serving it (~2027-10).
  var STOREFRONT_API = '/api/2026-10/graphql.json';
  var state = {
    loading: false,
    loaded: false,
    ready: false,
    timer: null,
    queue: [],
    touched: {},
    recovering: false
  };

  function warn() {
    if (window.console && console.warn) console.warn.apply(console, ['[LK SWYM Hybrid]'].concat([].slice.call(arguments)));
  }

  function toInt(value) {
    var n = parseInt(String(value || '').replace(/[^0-9]/g, ''), 10);
    return isNaN(n) ? null : n;
  }

  function productUrl(btn) {
    var handle = btn && (btn.dataset.productHandle || btn.getAttribute('data-product-handle'));
    if (handle) return window.location.origin + '/products/' + handle;
    return window.location.origin + '/products';
  }

  function itemFromButton(btn) {
    if (!btn) return null;
    var empi = toInt(btn.dataset.productId || btn.getAttribute('data-product-id'));
    var epi = toInt(btn.dataset.variantId || btn.getAttribute('data-variant-id'));
    if (!empi) return null;
    return { empi: empi, epi: epi || empi, du: productUrl(btn) };
  }

  // Same rule as the embed of this store (Ghost-Intent explicit consent is on): with the Shopify
  // Customer Privacy API loaded, the SDK needs preferences consent. Without the API, do not block
  // (the embed would block; design.md 3.5). An API that throws counts as refused.
  function consentAllows() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return true;
    try { return !!privacy.preferencesProcessingAllowed(); } catch (e) { return false; }
  }

  function ensureSwymGlobals() {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.swymLandingURL = window.swymLandingURL || document.URL;
    window.SwymPageData = window.SwymPageData || { et: 0, uri: document.URL };
    window.SwymProductVariants = window.SwymProductVariants || {};
    window.SwymProductInfo = window.SwymProductInfo || {};
    window.SwymHasCartItems = window.SwymHasCartItems || false;
    // The SDK reads swymCart.token, unguarded, right before it validates the customer: without it, it
    // throws and the device never links (the embed renders {{ cart | json }}). The SDK refreshes it
    // itself on later /cart.js calls.
    window.swymCart = window.swymCart || { token: null, item_count: 0, items: [] };
    // As the embed: one wishlist per customer.
    window.SwymEnabledCommonFeatures = window.SwymEnabledCommonFeatures || { 'multiple-wishlist': false };
    // The same globals the wishlist-embed-init script of the embed sets.
    window.swymWishlistEmbedLoaded = true;
    // "Show UI" off: SDK only, no Swym buttons, launcher or drawer (the LK hearts are the UI).
    window._SwymSDKOnlyMode = true;
    window.SwymShopifyCdnInUse = true;
    window.SwymCurrentStorePath = cfg.storePath;
    window.SwymCurrentJSPath = cfg.jsPath;
    window.SwymAssetBaseUrl = cfg.sdkSrc.substring(0, cfg.sdkSrc.lastIndexOf('/') + 1);
    if (!window._swrc && cfg.retailerId) {
      (function (s, w, r) {
        r.SwymRetailerConfig = s;
        r[s] = r[s] || function (k, v) { r[s][k] = v; };
      })('_swrc', '', window);
      window._swrc('RetailerId', cfg.retailerId);
      // As the embed snippet: initSwymShopify is what links the device to window.swymCustomerId.
      window._swrc('Callback', function () {
        if (typeof window.initSwymShopify === 'function') window.initSwymShopify();
      });
    }
  }

  // SwymCallbacks can run before the SDK links the device to the logged-in customer, so wait until
  // the device is linked to THIS customer. isAlreadyAuth() only says some customer is (an email
  // cookie, which survives logout because the SDK never runs for visitors), and authCheck carries no
  // Shopify id (no regn.userinfo.extid on the 854 relay, seen 2026-10-17). So the link is the SDK's
  // own validateUserLogin keyed by this id, run once more here: its answer sets this customer's Swym
  // email and regid. It calls back on error too, so the email is cleared first and only a validation
  // that worked sets it again. A validation of the SDK's own still settling (regid just changed) ends
  // with a /get for the old regid that writes the email back to null: run after its
  // sw:customerinforefreshed. After the timeout, send anyway (Swym merges on login), but as not linked.
  function afterCustomerSync(swat, done) {
    var cid = toInt(window.swymCustomerId);
    if (!cid || typeof swat.validateUserLogin !== 'function' || typeof swat.setSwymEmail !== 'function') return done(false);
    var finished = false;
    var finish = function (linked) {
      if (finished) return;
      finished = true;
      done(linked);
    };
    var started = false;
    var validate = function () {
      if (started) return;
      started = true;
      swat.setSwymEmail(null);
      swat.validateUserLogin('shopify', cid, function () { finish(swat.isAlreadyAuth()); });
    };
    // ponytail: _pendingCustomerInfoRefresh is SDK-internal (848/854); without it, validate right away.
    if (swat._pendingCustomerInfoRefresh && swat.evtLayer) {
      swat.evtLayer.addEventListener((swat.JSEvents && swat.JSEvents.customerInfoRefreshed) || 'sw:customerinforefreshed', validate);
    } else {
      validate();
    }
    setTimeout(function () {
      if (finished) return;
      warn('customer sync not confirmed; sending anyway');
      finish(false);
    }, cfg.timeoutMs || 8000);
  }

  function onSwymReady(swat) {
    if (state.loaded) return; // a retried injection registers a 2nd callback
    state.loaded = true;
    state.loading = false;
    clearTimeout(state.timer);
    afterCustomerSync(swat, function (linked) {
      state.ready = true;
      flushQueue(swat);
      // D-212 only with a confirmed link: never into an anonymous list or another customer's.
      if (linked) recoverDeviceFavorites(swat);
    });
  }

  function loadSwym() {
    if (state.ready) {
      window.SwymCallbacks.push(flushQueue);
      return state.promise;
    }
    if (state.loading || state.loaded) return state.promise;
    if (!consentAllows()) return Promise.reject(new Error('no consent to load SWYM'));
    if (!cfg.sdkSrc || !cfg.storePath) return Promise.reject(new Error('LK_SWYM_CONFIG incomplete'));
    state.loading = true;
    ensureSwymGlobals();

    state.promise = new Promise(function (resolve, reject) {
      // The SDK runs SwymCallbacks once its config is loaded.
      window.SwymCallbacks.push(function (swat) {
        onSwymReady(swat);
        resolve(swat);
      });

      // If someone turns the embed back on, it already injects the SDK: never inject a second one.
      // The embed's own guard looks for this id, so it would not inject a 2nd one either.
      var existing = document.getElementById('swym-ext-shopify-script') || document.querySelector('script[src*="swym-ext-shopify"]');
      if (!existing && !window._swat) {
        var s = document.createElement('script');
        s.id = 'swym-ext-shopify-script';
        s.async = true;
        s.defer = true;
        s.src = cfg.sdkSrc;
        s.onerror = function () {
          // Out of the DOM, so the next heart tap tries again.
          if (s.parentNode) s.parentNode.removeChild(s);
          clearTimeout(state.timer);
          state.loading = false;
          reject(new Error('SWYM SDK failed to load'));
        };
        document.head.appendChild(s);
      }

      // Only a warning: the queue stays and flushes if the SDK becomes ready later.
      var timeoutMs = cfg.timeoutMs || 8000;
      if (!state.loaded) state.timer = setTimeout(function () {
        warn('SDK not ready after ' + timeoutMs + ' ms; favorites stay on this device for now');
      }, timeoutMs);
    });
    return state.promise;
  }

  function sync(action, item) {
    if (!item) return;
    if (!consentAllows()) return; // without consent the heart stays on this device only
    state.touched[String(item.empi)] = true;
    state.queue.push({ action: action, item: item });
    loadSwym().catch(function (err) {
      warn(err.message || err);
    });
  }

  function flushQueue(swat) {
    if (!swat || !state.queue.length) return;
    var q = state.queue.splice(0, state.queue.length);
    // Only the last action per product: an add and a remove sent in the same tick race on Swym's side.
    var last = {};
    q.forEach(function (entry) { last[entry.item.empi] = entry; });
    q.forEach(function (entry) {
      if (last[entry.item.empi] !== entry) return;
      try {
        if (entry.action === 'remove' && typeof swat.removeFromWishList === 'function') {
          swat.removeFromWishList(entry.item, function () {});
        } else if (entry.action === 'add' && typeof swat.addToWishList === 'function') {
          swat.addToWishList(entry.item, function () {});
        }
      } catch (e) {
        warn('sync failed', e);
      }
    });
  }

  function readStorage(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function writeStorage(key, value) {
    try { window.localStorage.setItem(key, value); return true; } catch (e) { return false; }
  }

  function localFavoriteIds() {
    var list;
    try { list = JSON.parse(readStorage('lk_wishlist')); } catch (e) { list = null; }
    if (!Array.isArray(list)) list = [];
    var seen = {};
    return list.map(function (id) { return String(toInt(id) || ''); }).filter(function (id) {
      if (!id || seen[id]) return false;
      seen[id] = true;
      return true;
    });
  }

  // First variant and handle, like the card heart (product.variants[0]). Unknown products come back null.
  function lookupProducts(ids) {
    var gids = ids.map(function (id) { return '"gid://shopify/Product/' + id + '"'; }).join(',');
    var query = '{nodes(ids:[' + gids + ']){... on Product{id handle variants(first:1){nodes{id}}}}}';
    return fetch(STOREFRONT_API + '?query=' + encodeURIComponent(query), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('product lookup ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var nodes = (data && data.data && data.data.nodes) || [];
        return nodes.filter(function (p) {
          return p && p.handle && p.variants && p.variants.nodes && p.variants.nodes[0];
        }).map(function (p) {
          return {
            empi: toInt(p.id),
            epi: toInt(p.variants.nodes[0].id),
            du: window.location.origin + '/products/' + p.handle,
            cprops: { lk_source: 'device_recovery' }
          };
        });
      });
  }

  // D-212: favorites saved only on this device (lk_wishlist) while the loader was broken go to the
  // Swym list of the first customer linked on this device, once per device: a later customer on the
  // same device gets nothing. Never for visitors. Idempotent: only what the Swym list lacks is sent,
  // and the flag (written only when every add succeeded) stops a second run. The cprops mark reaches
  // Swym with each add; that Swym passes it on to the Klaviyo event is not proven (design.md §5).
  function recoverDeviceFavorites(swat) {
    if (!toInt(window.swymCustomerId) || state.recovering || !swat || typeof swat.fetch !== 'function') return;
    if (readStorage(RECOVERY_FLAG)) return;
    var finished = false;
    var done = function () {
      if (finished) return;
      finished = true;
      writeStorage(RECOVERY_FLAG, new Date().toISOString().slice(0, 10));
      state.recovering = false;
    };
    var local = localFavoriteIds();
    if (!local.length) return done();
    state.recovering = true;
    swat.fetch(function (items) {
      var saved = {};
      (items || []).forEach(function (it) { if (it && it.empi) saved[String(it.empi)] = true; });
      // ponytail: only the newest RECOVERY_CAP (lk_wishlist appends); older ones stay on the device.
      var missing = local.filter(function (id) { return !saved[id] && !state.touched[id]; }).slice(-RECOVERY_CAP);
      if (!missing.length) return done();
      lookupProducts(missing).then(function (products) {
        // ponytail: the SDK calls back only on success, so a failed add never writes the flag and the
        // next SDK load retries what Swym still lacks.
        var i = 0, sent = 0, saved = 0;
        var settle = function () { if (i >= products.length && saved === sent) done(); };
        (function next() {
          if (i >= products.length) return settle();
          var item = products[i++];
          if (state.touched[String(item.empi)]) return next(); // tapped meanwhile: the tap wins
          sent++;
          try {
            swat.addToWishList(item, function () { saved++; settle(); });
          } catch (e) { warn('recovery failed', e); }
          setTimeout(next, RECOVERY_GAP_MS);
        })();
      }).catch(function (err) {
        state.recovering = false;
        warn('recovery skipped', err.message || err);
      });
    });
  }

  function autoTries() {
    var t = (readStorage(AUTO_TRY_KEY) || '').split('|');
    return { at: Date.parse(t[0]), count: parseInt(t[1], 10) || 0 };
  }

  // D-243: pending = logged-in customer, device favorites, no recovery flag, an automatic try left and
  // none in the last 24 h (Math.abs: a clock set ahead does not block for good).
  function recoveryPending() {
    if (!toInt(window.swymCustomerId) || readStorage(RECOVERY_FLAG) || !localFavoriteIds().length) return false;
    var tries = autoTries();
    return tries.count < AUTO_MAX_TRIES && !(Math.abs(Date.now() - tries.at) < AUTO_BACKOFF_MS);
  }

  // ponytail: the try is recorded before the load, so any failure (download, link not confirmed, lookup,
  // add) or a page left mid-recovery uses one of the tries and waits 24 h; a heart tap still recovers.
  function autoRecover() {
    if (state.loading || state.loaded || !consentAllows() || !recoveryPending()) return;
    // Storage that cannot keep the try (full, blocked): no automatic load, or it would run on every page.
    if (!writeStorage(AUTO_TRY_KEY, new Date().toISOString() + '|' + (autoTries().count + 1))) return;
    loadSwym().catch(function (err) {
      warn(err.message || err);
    });
  }

  // After the load event, a delay and an idle period: never competes with LCP or the load's main thread.
  function whenIdleAfterLoad(fn) {
    var idle = function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: AUTO_DELAY_MS });
      else fn();
    };
    var later = function () { setTimeout(idle, AUTO_DELAY_MS); };
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later);
  }

  // Capture before current LK localStorage click handler stops propagation.
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.pc__wishlist, .pdp-gallery__wishlist, [data-lk-wishlist]');
    if (!btn) return;
    var item = itemFromButton(btn);
    if (!item) return;
    var willRemove = btn.classList.contains('swym-added');
    sync(willRemove ? 'remove' : 'add', item);
  }, true);

  // Optional: load only on explicit wishlist page, not on home/collection first paint.
  if (/\/pages\/my-wishlist/i.test(window.location.pathname)) {
    loadSwym().catch(function () {});
  } else if (recoveryPending()) {
    whenIdleAfterLoad(autoRecover);
  }

  window.LKSwymHybrid = {
    load: loadSwym,
    sync: sync,
    flush: function () { if (state.ready) window.SwymCallbacks.push(flushQueue); },
    state: state
  };
})();
